import ReactGA from 'react-ga4';
import { setClarityFunnelStep } from './clarity';

// Event parameter types for better TypeScript support
export interface GAEventParams {
  event_category?: string;
  event_label?: string;
  value?: number;
  currency?: string;
  custom_parameter?: string;
  interaction_type?: string;
  cta_location?: string;
  cta_text?: string;
  plan_selected?: string;
  plan_price?: string;
  form_type?: string;
  source?: string;
  application_type?: string;
  percent_scrolled?: number;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  // Additional common params used across app
  modal_type?: string;
  variant?: string;
  organization_id?: string;
  bot_id?: string;
  url_count?: number;
  scrape_failed?: boolean;
  trigger?: string;
  error_type?: string;
}

/**
 * Initialize Google Analytics 4
 *
 * Inline gtag en _document.tsx ya hace `gtag('config', 'G-TQKZZX698Q', ...)`
 * sincronamente. Acá solo registramos ReactGA wrapper si la página lo necesita,
 * sin re-llamar config (evita duplicate pageviews).
 */
export const initializeGA4 = (): void => {
  if (typeof window === 'undefined') return;
  // gtag ya inicializado por _document.tsx — no-op aquí.
  // ReactGA.gtag delega a window.gtag de cualquier forma.
};

/** App routes excluded from GA4 except these explicit allowlist entries. */
export const GA4_TRACKED_BOT_PATHS = ['/bot/create'] as const;

/**
 * Whether to send GA4 page_view for a Next.js pathname.
 * Marketing + dashboard routes are tracked; most /bot/* are not (noise).
 */
export function shouldTrackAppPageView(pathname: string): boolean {
  if (!pathname.startsWith('/bot')) {
    return true;
  }
  return (GA4_TRACKED_BOT_PATHS as readonly string[]).includes(pathname);
}

/**
 * Track page view for GA4 — fires `page_view` event explícito.
 *
 * Antes usaba `gtag('config', ID, {...})` que en SPA puede o no disparar
 * pageview (race conditions). Cambio a `gtag('event', 'page_view')` que
 * dispara siempre y respeta page_path correctamente.
 */
export const trackPageView = (pathname: string, title?: string): void => {
  if (typeof window === 'undefined') return;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const gtag = (window as any).gtag;
  if (typeof gtag !== 'function') return;

  try {
    gtag('event', 'page_view', {
      page_title: title || getPageTitle(pathname),
      page_path: pathname,
      page_location: window.location.href,
    });
  } catch (error) {
    console.warn('Page view tracking failed:', error);
  }
};

/**
 * Get page title consistently across routers
 * @param fallback - Fallback string (usually pathname)
 * @returns Page title or fallback
 */
export const getPageTitle = (fallback: string): string => {
  if (typeof window === 'undefined') return fallback;
  return document.title || fallback;
};

// CTA click tracking parameters
export interface CTAClickParams extends GAEventParams {
  cta_location: string;
  cta_text?: string;
}

// Trial request parameters
export interface TrialRequestParams extends GAEventParams {
  plan_selected: string;
  plan_price?: string;
}

// Lead form submission parameters
export interface LeadFormParams extends GAEventParams {
  form_type: string;
  source: string;
}

// Partner application parameters
export interface PartnerApplicationParams extends GAEventParams {
  application_type: string;
  source: string;
}

// Calendar interaction parameters
export interface CalendarInteractionParams extends GAEventParams {
  interaction_type: string;
}

/**
 * Track a custom event in Google Analytics 4
 * @param eventName - The name of the event
 * @param parameters - Event parameters object
 */
export const trackEvent = (eventName: string, parameters?: GAEventParams): void => {
  try {
    ReactGA.gtag('event', eventName, parameters);
  } catch (error) {
    console.warn('Analytics tracking failed:', error);
  }
};

/**
 * Track CTA button clicks
 * @param params - CTA click parameters
 */
export const trackCTAClick = (params: CTAClickParams): void => {
  try {
    ReactGA.gtag('event', 'cta_click', {
      event_category: 'conversion',
      ...params
    });
  } catch (error) {
    console.warn('Analytics tracking failed:', error);
  }
};

/**
 * Track trial requests (with capital T to match GA4 event)
 * @param params - Trial request parameters
 */
export const trackTrialRequest = (params: TrialRequestParams): void => {
  try {
    ReactGA.gtag('event', 'Trial_request', {
      event_category: 'conversion',
      ...params
    });
  } catch (error) {
    console.warn('Analytics tracking failed:', error);
  }
};

/**
 * Track lead form submissions
 * @param params - Lead form parameters
 */
export const trackLeadFormSubmit = (params: LeadFormParams): void => {
  try {
    ReactGA.gtag('event', 'lead_form_submit', {
      event_category: 'conversion',
      ...params
    });
  } catch (error) {
    console.warn('Analytics tracking failed:', error);
  }
};

/**
 * Track partner applications
 * @param params - Partner application parameters
 */
export const trackPartnerApplication = (params: PartnerApplicationParams): void => {
  try {
    ReactGA.gtag('event', 'partner_application_submit', {
      event_category: 'conversion',
      ...params
    });
  } catch (error) {
    console.warn('Analytics tracking failed:', error);
  }
};

/**
 * Track calendar interactions
 * @param params - Calendar interaction parameters
 */
export const trackCalendarInteraction = (params: CalendarInteractionParams): void => {
  try {
    ReactGA.gtag('event', 'calendar_interaction', {
      event_category: 'conversion',
      ...params
    });
  } catch (error) {
    console.warn('Analytics tracking failed:', error);
  }
};

/**
 * Track scroll engagement
 * @param percent_scrolled - Percentage of page scrolled
 */
export const trackScrollEngagement = (percent_scrolled: number): void => {
  try {
    ReactGA.gtag('event', 'scroll', {
      event_category: 'engagement',
      percent_scrolled
    });
  } catch (error) {
    console.warn('Analytics tracking failed:', error);
  }
};

/**
 * Track demo interactions
 * @param interaction_type - Type of demo interaction
 */
export const trackDemoInteraction = (interaction_type: string): void => {
  try {
    ReactGA.gtag('event', 'demo_interaction', {
      event_category: 'engagement',
      interaction_type
    });
  } catch (error) {
    console.warn('Analytics tracking failed:', error);
  }
};

const BOT_CREATE_FUNNEL_CATEGORY = 'bot_create_funnel';

export interface BotCreateFunnelParams extends GAEventParams {
  organization_id?: string;
  bot_id?: string;
  url_count?: number;
  scrape_failed?: boolean;
  trigger?: 'submit' | 'skip' | 'scrape_recovery';
  error_type?: string;
  cta_location?: string;
}

export const trackBotCreatePageViewed = (params?: Pick<BotCreateFunnelParams, 'organization_id'>): void => {
  trackEvent('bot_create_page_viewed', {
    event_category: BOT_CREATE_FUNNEL_CATEGORY,
    ...params,
  });
  setClarityFunnelStep('bot_create', 'page_viewed', {
    organization_id: params?.organization_id,
  });
};

export const trackCreateBotCtaClicked = (
  params: Pick<BotCreateFunnelParams, 'cta_location' | 'organization_id'>,
): void => {
  trackEvent('create_bot_cta_clicked', {
    event_category: BOT_CREATE_FUNNEL_CATEGORY,
    ...params,
  });
  setClarityFunnelStep('bot_create', 'cta_clicked', {
    cta_location: params.cta_location,
    organization_id: params.organization_id,
  });
};

export const trackBotCreateSubmitted = (
  params?: Pick<BotCreateFunnelParams, 'organization_id' | 'url_count'>,
): void => {
  trackEvent('bot_create_submitted', {
    event_category: BOT_CREATE_FUNNEL_CATEGORY,
    ...params,
  });
  setClarityFunnelStep('bot_create', 'submitted', {
    organization_id: params?.organization_id,
  });
};

export const trackBotCreateSkipped = (
  params?: Pick<BotCreateFunnelParams, 'organization_id'>,
): void => {
  trackEvent('bot_create_skipped', {
    event_category: BOT_CREATE_FUNNEL_CATEGORY,
    ...params,
  });
  setClarityFunnelStep('bot_create', 'skipped', {
    organization_id: params?.organization_id,
  });
};

export const trackBotDiscoverSuccess = (
  params: Pick<BotCreateFunnelParams, 'bot_id' | 'trigger' | 'organization_id' | 'scrape_failed'>,
): void => {
  trackEvent('bot_discover_success', {
    event_category: BOT_CREATE_FUNNEL_CATEGORY,
    ...params,
  });
  setClarityFunnelStep('bot_create', 'discover_success', {
    bot_id: params.bot_id,
    trigger: params.trigger,
    organization_id: params.organization_id,
    scrape_failed: params.scrape_failed,
  });
};

export const trackBotDiscoverFailed = (
  params: Pick<BotCreateFunnelParams, 'trigger' | 'organization_id' | 'error_type'>,
): void => {
  trackEvent('bot_discover_failed', {
    event_category: BOT_CREATE_FUNNEL_CATEGORY,
    ...params,
  });
  setClarityFunnelStep('bot_create', 'discover_failed', {
    trigger: params.trigger,
    organization_id: params.organization_id,
    error_type: params.error_type,
  });
};

const AUTH_OTP_FUNNEL_CATEGORY = 'auth_otp_funnel';

export type AuthOtpSource = 'confirmation' | 'login';

export interface AuthOtpFunnelParams extends GAEventParams {
  source: AuthOtpSource;
  has_email_param?: boolean;
  error_type?: string;
}

export const trackTrialConfirmationViewed = (
  params?: Pick<AuthOtpFunnelParams, 'has_email_param'>,
): void => {
  trackEvent('trial_confirmation_viewed', {
    event_category: AUTH_OTP_FUNNEL_CATEGORY,
    source: 'confirmation',
    ...params,
  });
  setClarityFunnelStep('trial_auth', 'confirmation_viewed', {
    funnel_source: 'confirmation',
    has_email_param: params?.has_email_param,
  });
};

export const trackAuthOtpSubmitted = (
  params: Pick<AuthOtpFunnelParams, 'source'>,
): void => {
  trackEvent('confirmation_otp_submitted', {
    event_category: AUTH_OTP_FUNNEL_CATEGORY,
    ...params,
  });
  setClarityFunnelStep('trial_auth', 'otp_submitted', {
    funnel_source: params.source,
  });
};

export const trackAuthLoginSuccess = (
  params: Pick<AuthOtpFunnelParams, 'source'>,
): void => {
  trackEvent('confirmation_login_success', {
    event_category: AUTH_OTP_FUNNEL_CATEGORY,
    ...params,
  });
  setClarityFunnelStep('trial_auth', 'login_success', {
    funnel_source: params.source,
  });
};

export const trackAuthLoginFailed = (
  params: Pick<AuthOtpFunnelParams, 'source' | 'error_type'>,
): void => {
  trackEvent('confirmation_login_failed', {
    event_category: AUTH_OTP_FUNNEL_CATEGORY,
    ...params,
  });
  setClarityFunnelStep('trial_auth', 'login_failed', {
    funnel_source: params.source,
    error_type: params.error_type,
  });
};

export const trackAuthCodeResent = (
  params: Pick<AuthOtpFunnelParams, 'source'>,
): void => {
  trackEvent('confirmation_code_resent', {
    event_category: AUTH_OTP_FUNNEL_CATEGORY,
    ...params,
  });
  setClarityFunnelStep('trial_auth', 'code_resent', {
    funnel_source: params.source,
  });
};

export const trackConfirmationOpenMailClicked = (): void => {
  trackEvent('confirmation_open_mail_clicked', {
    event_category: AUTH_OTP_FUNNEL_CATEGORY,
    source: 'confirmation',
  });
};

export const trackConfirmationOtpPasted = (): void => {
  trackEvent('confirmation_otp_pasted', {
    event_category: AUTH_OTP_FUNNEL_CATEGORY,
    source: 'confirmation',
  });
};

export const trackConfirmationFormReady = (
  params: Pick<AuthOtpFunnelParams, 'has_email_param'>,
): void => {
  trackEvent('confirmation_form_ready', {
    event_category: AUTH_OTP_FUNNEL_CATEGORY,
    source: 'confirmation',
    ...params,
  });
};

/**
 * Get UTM parameters from current URL
 * @returns UTM parameters object
 */
export const getUTMParams = () => {
  if (typeof window === 'undefined') return {};
  
  const urlParams = new URLSearchParams(window.location.search);
  return {
    utm_source: urlParams.get('utm_source'),
    utm_medium: urlParams.get('utm_medium'),
    utm_campaign: urlParams.get('utm_campaign'),
    utm_term: urlParams.get('utm_term'),
    utm_content: urlParams.get('utm_content')
  };
};

/**
 * Track UTM parameters automatically
 */
export const trackUTMParams = (): void => {
  const utmParams = getUTMParams();
  
  if (utmParams.utm_source) {
    try {
      ReactGA.gtag('event', 'utm_tracking', {
        event_category: 'campaign',
        ...utmParams
      });
    } catch (error) {
      console.warn('UTM tracking failed:', error);
    }
  }
};